"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

const BLOG_BINDINGS = {
  "component.options.articles": "state.articles",
} as const;

export const blogInputs = [
  {
    name: "title",
    type: "string",
    required: true,
    defaultValue: "Latest Blog Posts",
  },
  {
    name: "errorMessage",
    type: "string",
    required: true,
    defaultValue: "Unable to load articles",
  },
  {
    name: "disableTiers",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "reversed",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "disableLoyalty",
    type: "boolean",
    defaultValue: true,
  },
  {
    name: "articles",
    type: "list",
    subFields: [
      {
        name: "title",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
      {
        name: "subtitle",
        type: "string",
        required: true,
      },
      {
        name: "articleType",
        type: "string",
        enum: ["article", "video", "quiz"],
        defaultValue: "article",
      },
      {
        name: "controlColor",
        type: "string",
        enum: ["dark", "light"],
        defaultValue: "dark",
      },
      {
        name: "articleId",
        type: "string",
      },
      {
        name: "image",
        type: "object",
        subFields: [
          {
            name: "smallReference",
            type: "file",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
            required: true,
          },
          {
            name: "mediumReference",
            type: "file",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
            required: true,
          },
          {
            name: "largeReference",
            type: "file",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
            required: true,
          },
          {
            name: "altText",
            type: "string",
            required: true,
          },
        ],
        required: false,
      },
      {
        name: "readTime",
        type: "string",
        required: true,
      },
      {
        name: "creationDate",
        type: "string",
        required: true,
      },
      {
        name: "path",
        type: "string",
        required: true,
      },
      {
        name: "showAuthor",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "author",
        type: "string",
        showIf: `options.get("showAuthor") === "true"`,
      },
    ],
    friendlyName: "Blog Articles",
    defaultValue: [],
    required: true,
  },
] as const satisfies Inputs;

export const BlogComponent: RegisteredComponent = {
  name: "Blog",
  component: dynamic(async () => withValidation(blogInputs, (await import("./Blog")).Blog, "Blog")),
  inputs: blogInputs,
  defaults: {
    bindings: BLOG_BINDINGS,
  },
};
